import { memo, useContext, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { AuthContext } from '../../../context/auth-context'
import { CubeLoader } from '../../../components/ui/cube-loader'
import { Badge } from '../../../components/ui/badge'
import { cn } from '../../../lib/utils'
import { GET_CURRENT_USER } from './queries'

interface SidebarProfileProps {
    collapsed: boolean
    environmentLabel: string
}

const buildInitials = (firstName?: string | null, lastName?: string | null, email?: string | null) => {
    const initials = [firstName, lastName]
        .filter(Boolean)
        .map((part) => part!.charAt(0).toUpperCase())
        .join('')

    if (initials) {
        return initials
    }

    if (email) {
        return email.charAt(0).toUpperCase()
    }

    return 'S'
}

interface SidebarUser {
    id: string
    email?: string | null
    firstName?: string | null
    lastName?: string | null
}

interface SidebarUserData {
    users: SidebarUser[]
}

interface SidebarUserVariables {
    where: { id?: string | null }
}

interface AuthUser {
    id?: string
    email?: string | null
}

const SidebarProfile = memo(({ collapsed, environmentLabel }: SidebarProfileProps) => {
    const auth = useContext(AuthContext) as { user: AuthUser | null }
    const authUser = auth?.user ?? null

    const { data, loading } = useQuery<SidebarUserData, SidebarUserVariables>(GET_CURRENT_USER, {
        skip: !authUser?.id,
        variables: {
            where: {
                id: authUser?.id,
            },
        },
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-first',
    })

    const details = data?.users?.[0]

    const name = useMemo(() => {
        const parts = [details?.firstName, details?.lastName].filter(Boolean)
        const fullName = parts.join(' ').trim()
        return fullName || 'Super Admin'
    }, [details?.firstName, details?.lastName])

    const initials = useMemo(
        () => buildInitials(details?.firstName, details?.lastName, details?.email ?? authUser?.email ?? null),
        [details?.email, details?.firstName, details?.lastName, authUser?.email]
    )

    return (
        <section className="tw:px-4 tw:pb-4 tw:pt-5" aria-labelledby="sidebar-profile-heading">
            <div
                className={cn(
                    'tw:flex tw:items-center tw:gap-3 tw:rounded-2xl tw:border tw:border-slate-800 tw:bg-slate-900/60 tw:px-3 tw:py-3 tw:transition-colors tw:duration-200',
                    collapsed ? 'tw:justify-center tw:flex-col tw:gap-2' : 'tw:flex-row'
                )}
            >
                <div className="tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-full tw:bg-blue-500/20 tw:text-base tw:font-semibold tw:text-blue-300">
                    {initials}
                </div>
                {!collapsed && (
                    <div className="tw:min-w-0 tw:flex-1 tw:space-y-1">
                        {loading ? (
                            <CubeLoader size="xs" />
                        ) : (
                            <>
                                <p className="tw:text-sm tw:font-medium tw:text-slate-100 tw:truncate" title={name}>
                                    {name}
                                </p>
                                <p className="tw:text-xs tw:text-slate-400 tw:truncate" title={details?.email || authUser?.email || ''}>
                                    {details?.email || authUser?.email || 'superadmin@turbamedia.io'}
                                </p>
                            </>
                        )}
                    </div>
                )}
                {collapsed ? (
                    <Badge
                        variant="info"
                        className="tw:min-w-[2.25rem] tw:justify-center tw:rounded-full tw:border-0 tw:bg-blue-500/20 tw:px-2 tw:py-1 tw:text-[0.65rem] tw:font-semibold tw:uppercase tw:text-blue-200"
                    >
                        {environmentLabel}
                    </Badge>
                ) : (
                    <Badge
                        variant="info"
                        className="tw:w-fit tw:rounded-full tw:border-0 tw:bg-blue-500/15 tw:px-2.5 tw:py-1 tw:text-[0.65rem] tw:font-semibold tw:uppercase tw:text-blue-200"
                    >
                        {environmentLabel}
                    </Badge>
                )}
            </div>
        </section>
    )
})

SidebarProfile.displayName = 'SidebarProfile'

export default SidebarProfile
